import {
  AlertCircle,
  AlertTriangle,
  ArrowDownLeft,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  ArrowUpDown,
  Building2,
  Check,
  Globe2,
  Lock,
  Save,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useWatch } from "react-hook-form";

import { countryName } from "@/modules/dashboard/cards/constants/countries.constants";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaCheckboxField } from "@/shared/components/forms/ColocaCheckboxField";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { colocaSurfaceVariants } from "@/shared/components/layout/colocaSurfaceVariants";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";
import { compareDecimalStrings, subtractDecimals } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";

import { useFeeLabels } from "../../../hooks/useFeeLabels";
import { useFeeMutations } from "../../../hooks/useFeeMutations";
import {
  PaymentDirection,
  PaymentFeeKind,
  type FeeScope,
  type PaymentFee,
  type PaymentFeeRail,
  type ProviderRailRoute,
} from "../../../types/paymentFees.types";
import { formatAmountText, formatFeeFormula, formatRangeLines, shortId } from "../../../utils/feeFormat";
import {
  computeFee,
  feeQueryFor,
  feeScopeOf,
  hasValidFromCollision,
  isZeroPrice,
  nextVersionFor,
  resolveFee,
  routesCoveredBy,
} from "../../../utils/feeMath";
import { organizationsKeepingOwnPrice } from "../../../utils/feeOverrides";
import { CorridorIdentity, CountryFlag } from "../CorridorIdentity";
import { scopePayload, toIsoInstant } from "../corridorPriceFormSchema";
import { candidatePrice, pricePayload } from "../feeFormSchema";
import { FeeTiersEditor } from "../FeeTiersEditor";
import {
  buildFeeRuleSchema,
  PRICE_STEP_FIELDS,
  seedFeeRuleForm,
  type FeeRuleFormValues,
  type FeeRuleStep,
} from "./feeRuleFormSchema";

export type FeeRuleMode = "new" | "version" | "duplicate";

export interface FeeRuleWizardProps {
  mode: FeeRuleMode;
  feeKind: PaymentFeeKind;
  source: PaymentFee | null;
  routes: ProviderRailRoute[];
  fees: PaymentFee[];
  now: number;
  scopeOrganizationId: string | null;
  scopeOrganizationName: string | null;
  organizationNames: Record<string, string>;
  exampleAmountFor: (currency: string | null | undefined) => string;
  onCancel: () => void;
  onCreated: (fee: PaymentFee) => void;
}

const FORM_ID = "fee-rule-form";

/** A rule that is not a row yet, so the resolver can tell where it would win. */
function draftRow(feeKind: PaymentFeeKind, scope: FeeScope, now: number): PaymentFee {
  const instant = new Date(now).toISOString();
  return {
    id: "__draft__",
    fee_kind: feeKind,
    ...scope,
    fixed: "0",
    percentage: "0",
    min_fee: "0",
    max_fee: null,
    tiers: null,
    valid_from: instant,
    valid_to: null,
    is_active: true,
    version: 0,
    created_by_user_id: null,
    change_reason: null,
    created_at: instant,
    updated_at: instant,
  };
}

export function FeeRuleWizard({
  mode,
  feeKind: initialKind,
  source,
  routes,
  fees,
  now,
  scopeOrganizationId,
  scopeOrganizationName,
  organizationNames,
  exampleAmountFor,
  onCancel,
  onCreated,
}: FeeRuleWizardProps) {
  const { t, activeLanguage } = useTranslations("paymentFees");
  const labels = useFeeLabels();
  const { isSubmitting, error: submitError, createFee } = useFeeMutations();

  const scopeLocked = mode === "version" && source != null;
  const kindLocked = scopeLocked || scopeOrganizationId != null;
  const steps: FeeRuleStep[] = kindLocked
    ? ["where", "price", "review"]
    : ["kind", "where", "price", "review"];
  const [step, setStep] = useState<FeeRuleStep>(steps[0]);
  const stepIndex = steps.indexOf(step);

  const schema = useMemo(
    () =>
      buildFeeRuleSchema({
        money: t("validation_money"),
        percentage: t("validation_percentage"),
        tierThreshold: t("validation_tier_threshold"),
        tierThresholdUnique: t("validation_tier_threshold_unique"),
        tierFixed: t("validation_tier_fixed"),
        tierPercentage: t("validation_tier_percentage"),
        validUntil: t("validation_valid_until"),
        validFromRequired: t("validation_valid_from_required"),
        reasonRequired: t("validation_reason_required"),
        ackRequired: t("validation_ack_required"),
      }),
    [t],
  );
  const [seed] = useState(() =>
    seedFeeRuleForm(scopeOrganizationId ? PaymentFeeKind.COLOCA_COMMISSION : initialKind, source),
  );
  const form = useZodForm(schema, seed);
  const values = useWatch({ control: form.control }) as FeeRuleFormValues;
  const feeKind = values.feeKind ?? seed.feeKind;
  const isCommission = feeKind === PaymentFeeKind.COLOCA_COMMISSION;

  /* ----------------------------------- where ----------------------------------- */

  const countries = useMemo(
    () =>
      [...new Set(routes.map((route) => route.country))].sort((a, b) =>
        countryName(a).localeCompare(countryName(b)),
      ),
    [routes],
  );
  const rails = useMemo(
    () =>
      [
        ...new Set(
          routes
            .filter((route) => !values.country || route.country === values.country)
            .map((route) => route.rail),
        ),
      ].sort((a, b) => formatRail(a).localeCompare(formatRail(b))),
    [routes, values.country],
  );

  const baseScope: FeeScope = useMemo(() => {
    if (scopeLocked && source) return feeScopeOf(source);
    const country = values.country || null;
    const rail = (values.rail || null) as PaymentFeeRail | null;
    const direction = (values.direction || null) as PaymentDirection | null;
    const matching = routes.filter(
      (route) =>
        (!country || route.country === country) &&
        (!rail || route.rail === rail) &&
        (!direction || route.direction === direction),
    );
    const currencies = [...new Set(matching.map((route) => route.currency))];
    const providers = [...new Set(matching.map((route) => route.provider_code))];
    return {
      provider_code: !isCommission && values.pinCostRoute && providers.length === 1 ? providers[0] : null,
      organization_id: isCommission ? scopeOrganizationId : null,
      rail,
      country,
      currency: country && currencies.length === 1 ? currencies[0] : null,
      direction,
    };
  }, [
    scopeLocked,
    source,
    values.country,
    values.rail,
    values.direction,
    values.pinCostRoute,
    routes,
    isCommission,
    scopeOrganizationId,
  ]);

  const covered = useMemo(() => routesCoveredBy(feeKind, baseScope, routes), [feeKind, baseScope, routes]);
  const uniqueProvider = useMemo(() => {
    const matching = routes.filter(
      (route) =>
        (!baseScope.country || route.country === baseScope.country) &&
        (!baseScope.rail || route.rail === baseScope.rail) &&
        (!baseScope.direction || route.direction === baseScope.direction),
    );
    return new Set(matching.map((route) => route.provider_code)).size === 1;
  }, [routes, baseScope]);

  const coverage = useMemo(() => {
    const draft = draftRow(feeKind, baseScope, now);
    const withDraft = [...fees, draft];
    return covered.map((route) => {
      const query = feeQueryFor(route, feeKind, isCommission ? scopeOrganizationId : null);
      const winner = resolveFee(query, withDraft, now);
      return { route, applies: winner?.id === draft.id, today: resolveFee(query, fees, now) };
    });
  }, [covered, feeKind, baseScope, fees, now, isCommission, scopeOrganizationId]);
  const applying = coverage.filter((item) => item.applies);

  /* ------------------------------- price and review ------------------------------- */

  const requiresAck = !(isCommission && baseScope.organization_id);
  useEffect(() => {
    form.setValue("requiresAck", requiresAck);
  }, [requiresAck, form]);

  const currencyLabel = baseScope.currency ?? t("currency_operation");
  const exampleAmount = exampleAmountFor(baseScope.currency);
  const candidate = candidatePrice(values);
  const validFromIso = values.effectiveNow ? null : toIsoInstant(values.validFromDate, values.validFromTime);
  const validToIso = toIsoInstant(values.validUntilDate, values.validUntilTime);
  const hasCollision = hasValidFromCollision(feeKind, baseScope, validFromIso, fees);
  const nextVersion = nextVersionFor(feeKind, baseScope, fees);

  const reviewRows = applying.map((item) => {
    const amount = exampleAmountFor(item.route.currency);
    return {
      route: item.route,
      amount,
      before: item.today ? (computeFee(item.today, amount)?.total ?? null) : null,
      after: computeFee(candidate, amount)?.total ?? null,
    };
  });
  const keptOrganizations =
    isCommission && !baseScope.organization_id
      ? organizationsKeepingOwnPrice(
          applying.map((item) => item.route),
          fees,
          now,
        )
      : [];
  const isZero = isCommission && isZeroPrice(candidate);
  const dropsByHalf =
    isCommission &&
    !isZero &&
    reviewRows.some(
      (row) =>
        row.before != null &&
        row.after != null &&
        compareDecimalStrings(row.before, "0") > 0 &&
        compareDecimalStrings(subtractDecimals(row.before, row.after), row.after) > 0,
    );

  const whoName = baseScope.organization_id
    ? (organizationNames[baseScope.organization_id] ??
      scopeOrganizationName ??
      shortId(baseScope.organization_id))
    : null;

  /* --------------------------------- navigation --------------------------------- */

  const [stepError, setStepError] = useState<string | null>(null);

  const goNext = async () => {
    setStepError(null);
    if (step === "where" && applying.length === 0) {
      setStepError(t("rule_validation_no_corridor"));
      return;
    }
    if (step === "price") {
      const valid = await form.trigger([...PRICE_STEP_FIELDS]);
      if (!valid || hasCollision) return;
    }
    setStep(steps[Math.min(stepIndex + 1, steps.length - 1)]);
  };

  const goBack = () => {
    setStepError(null);
    if (stepIndex === 0) onCancel();
    else setStep(steps[stepIndex - 1]);
  };

  const handleSubmit = async (submitted: FeeRuleFormValues) => {
    if (hasCollision) return;
    const reason = submitted.reason.trim();
    const created = await createFee({
      fee_kind: feeKind,
      ...scopePayload(baseScope),
      ...pricePayload(submitted),
      ...(validFromIso ? { valid_from: validFromIso } : {}),
      ...(validToIso ? { valid_to: validToIso } : {}),
      ...(reason ? { change_reason: reason } : {}),
    });
    if (created) onCreated(created);
  };

  const stepLabels: Record<FeeRuleStep, string> = {
    kind: t("rule_step_kind"),
    where: t("rule_step_where"),
    price: t("rule_step_price"),
    review: t("rule_step_review"),
  };

  const whenText = validFromIso
    ? t("rule_when_from", { date: formatDateTime(validFromIso, { locale: activeLanguage }) })
    : t("rule_when_now");

  return (
    <ColocaForm id={FORM_ID} form={form} onSubmit={handleSubmit} className="space-y-0">
      <div className="grid gap-6 lg:grid-cols-[13rem_minmax(0,1fr)_19rem]">
        <ol className="flex gap-2 overflow-x-auto lg:flex-col lg:gap-1">
          {steps.map((item, index) => {
            const done = index < stepIndex;
            const current = item === step;
            return (
              <li key={item}>
                <button
                  type="button"
                  disabled={index > stepIndex}
                  onClick={() => index < stepIndex && setStep(item)}
                  className={cn(
                    "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm whitespace-nowrap transition-colors",
                    current && "bg-primary/10 font-semibold text-primary",
                    !current && done && "text-foreground hover:bg-muted",
                    !current && !done && "text-muted-foreground",
                  )}
                >
                  <span
                    className={cn(
                      "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs",
                      current && "border-primary bg-primary text-primary-foreground",
                      done && "border-primary/40 text-primary",
                      !current && !done && "border-border",
                    )}
                  >
                    {done ? <Check className="size-3.5" aria-hidden="true" /> : index + 1}
                  </span>
                  {stepLabels[item]}
                </button>
              </li>
            );
          })}
        </ol>

        <div className={cn(colocaSurfaceVariants(), "min-w-0 space-y-6 p-5 sm:p-6")}>
          {step === "kind" && (
            <section className="space-y-4">
              <StepHeading title={t("rule_kind_title")} />
              <ColocaChoiceCards
                aria-label={t("rule_kind_title")}
                columns={2}
                value={feeKind}
                onValueChange={(value) => form.setValue("feeKind", value as PaymentFeeKind)}
                items={[
                  {
                    value: PaymentFeeKind.COLOCA_COMMISSION,
                    label: t("kind_commission"),
                    description: t("dialog_kind_commission_description"),
                    icon: <TrendingUp className="size-5 text-success" />,
                  },
                  {
                    value: PaymentFeeKind.PROVIDER_COST,
                    label: t("kind_cost"),
                    description: t("dialog_kind_cost_description"),
                    icon: <TrendingDown className="size-5 text-destructive" />,
                  },
                ]}
              />
              {isCommission && <WhoStrip name={whoName} />}
            </section>
          )}

          {step === "where" && (
            <section className="space-y-5">
              <StepHeading
                title={t("rule_where_title")}
                hint={scopeLocked ? t("dialog_scope_locked") : t("rule_where_hint")}
              />

              {kindLocked && isCommission && <WhoStrip name={whoName} />}

              {scopeLocked ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Lock className="size-3.5" aria-hidden="true" />
                  {t("dialog_scope_locked")}
                </p>
              ) : (
                <>
                  <ChipGroup
                    label={t("rule_country_label")}
                    value={values.country ?? ""}
                    onChange={(country) => {
                      form.setValue("country", country);
                      form.setValue("rail", "");
                    }}
                    options={[
                      { value: "", label: t("rule_country_all"), icon: <Globe2 className="size-3.5" /> },
                      ...countries.map((country) => ({
                        value: country,
                        label: countryName(country),
                        icon: <CountryFlag iso3={country} />,
                      })),
                    ]}
                  />
                  <ChipGroup
                    label={t("rule_rail_label")}
                    value={values.rail ?? ""}
                    onChange={(rail) => form.setValue("rail", rail)}
                    options={[
                      { value: "", label: t("rule_rail_all") },
                      ...rails.map((rail) => ({ value: rail, label: formatRail(rail) })),
                    ]}
                  />
                  <ChipGroup
                    label={t("rule_direction_label")}
                    value={values.direction ?? ""}
                    onChange={(direction) => form.setValue("direction", direction)}
                    options={[
                      {
                        value: "",
                        label: t("rule_direction_both"),
                        icon: <ArrowUpDown className="size-3.5" />,
                      },
                      {
                        value: PaymentDirection.PAYIN,
                        label: t("direction_payin"),
                        icon: <ArrowDownLeft className="size-3.5 text-success" />,
                      },
                      {
                        value: PaymentDirection.PAYOUT,
                        label: t("direction_payout"),
                        icon: <ArrowUpRight className="size-3.5 text-blue-700 dark:text-blue-300" />,
                      },
                    ]}
                  />
                  {!isCommission && uniqueProvider && (
                    <ColocaCheckboxField
                      name="pinCostRoute"
                      label={t("rule_cost_pin")}
                      description={t("rule_cost_pin_hint")}
                    />
                  )}
                </>
              )}

              <div className="space-y-2">
                <p className="text-sm font-medium">{t("rule_covers", { count: covered.length })}</p>
                {covered.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t("rule_covers_none")}</p>
                ) : (
                  <ul className="divide-y divide-border rounded-xl border border-border">
                    {coverage.map((item) => (
                      <li
                        key={item.route.id}
                        className="flex flex-wrap items-center justify-between gap-2 px-3 py-2"
                      >
                        <CorridorIdentity route={item.route} />
                        {item.applies ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
                            <Check className="size-3.5" aria-hidden="true" />
                            {t("rule_applies")}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t("rule_overridden")}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </section>
          )}

          {step === "price" && (
            <section className="space-y-6">
              <StepHeading title={t("rule_price_title")} />
              <FeeTiersEditor currencyLabel={currencyLabel} exampleAmount={exampleAmount}>
                <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
                  <ColocaAmountInput
                    name="percentage"
                    label={
                      values.useTiers
                        ? `${t("price_base_title")} · ${t("price_percentage_label")}`
                        : t("price_percentage_label")
                    }
                    suffix="%"
                    maxFractionDigits={4}
                    placeholder="0"
                    description={t("price_percentage_helper")}
                  />
                  <ColocaAmountInput
                    name="fixed"
                    label={
                      values.useTiers
                        ? `${t("price_base_title")} · ${t("price_fixed_label")}`
                        : t("price_fixed_label")
                    }
                    suffix={currencyLabel}
                    maxFractionDigits={8}
                    groupThousands
                    placeholder="0"
                    description={t("price_fixed_helper")}
                  />
                  <ColocaAmountInput
                    name="minFee"
                    label={t("price_min_label")}
                    suffix={currencyLabel}
                    maxFractionDigits={8}
                    groupThousands
                    placeholder="0"
                    description={t("price_min_helper")}
                  />
                  <ColocaAmountInput
                    name="maxFee"
                    label={t("price_max_label")}
                    suffix={currencyLabel}
                    maxFractionDigits={8}
                    groupThousands
                    placeholder={t("price_max_placeholder")}
                    description={t("price_max_helper")}
                  />
                </div>
              </FeeTiersEditor>

              <div className="space-y-3 border-t border-border pt-5">
                <p className="text-sm font-semibold">{t("rule_when_title")}</p>
                <label className="flex items-center gap-2.5 text-sm font-medium">
                  <ColocaSwitch
                    checked={values.effectiveNow ?? true}
                    onCheckedChange={(checked) => form.setValue("effectiveNow", checked)}
                    aria-label={t("eff_now_label")}
                  />
                  {t("eff_now_label")}
                </label>
                {!values.effectiveNow && (
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="w-40">
                      <ColocaInputField name="validFromDate" type="date" label={t("eff_date_label")} />
                    </div>
                    <div className="w-32">
                      <ColocaInputField name="validFromTime" type="time" label={t("eff_time_label")} />
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-start gap-2">
                  <div className="w-40">
                    <ColocaInputField
                      name="validUntilDate"
                      type="date"
                      label={t("valid_until_label")}
                      description={t("valid_until_helper")}
                    />
                  </div>
                  <div className="w-32">
                    <ColocaInputField name="validUntilTime" type="time" label={t("eff_time_label")} />
                  </div>
                </div>
                {hasCollision && (
                  <ColocaAlert variant="destructive" icon={AlertCircle} text={t("warn_collision")} />
                )}
                {nextVersion > 1 && (
                  <p className="text-xs text-muted-foreground">
                    {t("version_notice", { next: nextVersion, previous: nextVersion - 1 })}
                  </p>
                )}
              </div>
            </section>
          )}

          {step === "review" && (
            <section className="space-y-5">
              <StepHeading title={t("review_title")} />
              <p className="text-sm">{t("rule_review_applies_on", { count: reviewRows.length })}</p>
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full min-w-[28rem] text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">{t("review_th_corridor")}</th>
                      <th className="px-3 py-2 text-right font-medium">{t("review_th_before")}</th>
                      <th className="px-3 py-2 text-right font-medium">{t("review_th_after")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewRows.map((row) => (
                      <tr key={row.route.id} className="border-t border-border">
                        <td className="px-3 py-2">
                          <CorridorIdentity route={row.route} />
                          <span className="block text-[11px] text-muted-foreground">
                            {t("edit_compare_amount", {
                              amount: `${formatAmountText(row.amount, activeLanguage)} ${row.route.currency}`,
                            })}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {row.before == null
                            ? "—"
                            : `${formatAmountText(row.before, activeLanguage)} ${row.route.currency}`}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold tabular-nums text-primary">
                          {row.after == null
                            ? "—"
                            : `${formatAmountText(row.after, activeLanguage)} ${row.route.currency}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {isZero && (
                <ColocaAlert variant="destructive" icon={AlertCircle} text={t("review_zero_warning")} />
              )}
              {dropsByHalf && (
                <ColocaAlert variant="warning" icon={AlertTriangle} text={t("review_drop_warning")} />
              )}

              {isCommission &&
                !baseScope.organization_id &&
                (keptOrganizations.length > 0 ? (
                  <div className="space-y-1.5 text-sm">
                    <p>{t("review_orgs_kept", { count: keptOrganizations.length })}</p>
                    <p className="text-muted-foreground">
                      {keptOrganizations.map((id) => organizationNames[id] ?? shortId(id)).join(", ")}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t("review_orgs_none")}</p>
                ))}

              <ColocaTextareaField
                name="reason"
                label={t("reason_label")}
                description={t("reason_helper")}
                placeholder={t("reason_placeholder")}
                rows={2}
                maxLength={500}
                count
              />
              {requiresAck && (
                <ColocaCheckboxField
                  name="acknowledged"
                  label={t(isCommission ? "review_ack" : "review_ack_cost")}
                />
              )}
              {submitError && <ColocaAlert variant="destructive" icon={AlertCircle} text={submitError} />}
            </section>
          )}

          {stepError && <ColocaAlert variant="warning" icon={AlertTriangle} text={stepError} />}

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border pt-4">
            <ColocaButton
              type="button"
              variant="ghost"
              icon={ArrowLeft}
              onClick={goBack}
              disabled={isSubmitting}
            >
              {stepIndex === 0 ? t("dialog_cancel") : t("rule_back_step")}
            </ColocaButton>
            {step === "review" ? (
              <ColocaButton
                type="submit"
                variant="primary"
                icon={Save}
                loading={isSubmitting}
                disabled={hasCollision}
              >
                {t("rule_save")}
              </ColocaButton>
            ) : (
              <ColocaButton
                type="button"
                variant="primary"
                iconTrailing={ArrowRight}
                onClick={() => void goNext()}
              >
                {t("rule_next")}
              </ColocaButton>
            )}
          </div>
        </div>

        <aside className={cn(colocaSurfaceVariants(), "h-fit space-y-4 p-5 lg:sticky lg:top-4")}>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {t("rule_summary_title")}
          </p>
          <SummaryRow label={t("rule_summary_kind")}>
            {isCommission ? t("kind_commission") : t("kind_cost")}
          </SummaryRow>
          {isCommission && (
            <SummaryRow label={t("rule_summary_who")}>
              {whoName ? t("dialog_who_special", { name: whoName }) : t("dialog_who_general")}
            </SummaryRow>
          )}
          <SummaryRow label={t("rule_summary_where")}>
            {t("rule_summary_where_value", { applies: applying.length, total: covered.length })}
          </SummaryRow>
          <SummaryRow label={t("rule_summary_price")}>
            <span className="font-semibold tabular-nums">
              {formatFeeFormula(
                { ...candidate, currency: baseScope.currency } as PaymentFee,
                activeLanguage,
                labels.formula,
              )}
            </span>
            {candidate.tiers && (
              <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                {formatRangeLines(
                  { ...candidate, currency: baseScope.currency },
                  activeLanguage,
                  labels.formula,
                ).map((line) => (
                  <li key={line.label}>
                    {line.label}: <span className="text-foreground">{line.price}</span>
                  </li>
                ))}
              </ul>
            )}
          </SummaryRow>
          <SummaryRow label={t("rule_summary_when")}>{whenText}</SummaryRow>
          {source && (
            <p className="border-t border-border pt-3 text-xs text-muted-foreground">
              {t("rule_summary_source", {
                version: source.version,
                price: formatFeeFormula(source, activeLanguage, labels.formula),
              })}
            </p>
          )}
        </aside>
      </div>
    </ColocaForm>
  );
}

function StepHeading({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="space-y-1">
      <h2 className="text-lg font-semibold">{title}</h2>
      {hint && <p className="text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

function WhoStrip({ name }: { name: string | null }) {
  const { t } = useTranslations("paymentFees");
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium",
        name ? "bg-primary/10 text-primary" : "bg-muted",
      )}
    >
      {name ? (
        <Building2 className="size-4" aria-hidden="true" />
      ) : (
        <Globe2 className="size-4" aria-hidden="true" />
      )}
      {name ? t("dialog_who_special", { name }) : t("dialog_who_general")}
    </div>
  );
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm">{children}</div>
    </div>
  );
}

interface ChipOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

function ChipGroup({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: ChipOption[];
}) {
  return (
    <div className="space-y-2">
      <ColocaLabel>{label}</ColocaLabel>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
        {options.map((option) => {
          const selected = option.value === value;
          return (
            <button
              key={option.value || "__all__"}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(option.value)}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none",
                selected
                  ? "border-primary bg-primary/10 font-medium text-primary"
                  : "border-border text-foreground hover:bg-muted",
              )}
            >
              {option.icon}
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
